import { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { CalcShell } from "@/components/calculator/CalcShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

const Status = ({ icon, title, text }) => (
  <div className="text-center py-6">
    {icon}
    <h2 className="text-xl font-bold text-gray-900 mt-3" data-testid="payment-status-title">{title}</h2>
    <p className="text-gray-500 text-sm mt-2">{text}</p>
    <Link to="/"><Button className="mt-6 bg-[#102692]" data-testid="payment-home-btn">Retour à l'accueil</Button></Link>
  </div>
);

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(pathname.endsWith("cancel") ? "cancel" : sessionId ? "checking" : "failed");
  const [amount, setAmount] = useState(null);
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (state !== "checking" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setAmount(res.data.amount);
        setKind(res.data.kind);
        if (res.data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(res.data.payment_status)) return setState("failed");
        if (res.data.status === "processing") return setState("pending");
      } catch (e) {
        return setState("failed");
      }
      if (++attempts < 10) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId, state]);

  return (
    <CalcShell title="Souscription" stepIndex={0} totalSteps={0} stepKey={state}>
      <div data-testid={`payment-${state}`}>
        {state === "checking" && <Status icon={<Loader2 className="w-12 h-12 animate-spin text-[#102692] mx-auto" />} title="Vérification du paiement…" text="Merci de patienter quelques secondes." />}
        {state === "paid" && <Status icon={<CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />} title="Paiement confirmé" text={kind === "temporaire"
          ? `${amount ? eur(amount) + " réglés. " : ""}Votre reçu vous a été envoyé par email. Votre attestation d'assurance temporaire vous est envoyée très rapidement.`
          : `${amount ? eur(amount) + " réglés. " : ""}Votre reçu vous a été envoyé par email et les mensualités suivantes seront prélevées automatiquement avec le même moyen de paiement. Un conseiller vous contacte pour finaliser votre contrat.`} />}
        {state === "pending" && <Status icon={<Loader2 className="w-12 h-12 text-[#102692] mx-auto" />} title="Paiement en cours de traitement" text="Un prélèvement SEPA prend quelques jours ouvrés. Vous recevrez votre reçu par email dès sa validation." />}
        {state === "failed" && <Status icon={<XCircle className="w-14 h-14 text-red-500 mx-auto" />} title="Paiement non abouti" text="Aucun montant n'a été prélevé. Appelez-nous au 01 48 20 98 27." />}
        {state === "cancel" && <Status icon={<XCircle className="w-14 h-14 text-gray-400 mx-auto" />} title="Paiement annulé" text="Votre demande est conservée, un conseiller peut vous rappeler." />}
      </div>
    </CalcShell>
  );
}
